import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import {
  MapPin,
  Truck,
  Clock,
  Gauge,
  Users,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Wrench,
  X,
  Phone,
  User,
  ClipboardCopy,
  Navigation,
  ExternalLink,
  CalendarClock,
  MapPinned,
  Compass,
} from 'lucide-react';
import { useGoogleMaps, useDirectionsCache, useMapBounds } from './useGoogleMaps';
import MapDiagnosticsBanner from './MapDiagnosticsBanner';

const API = process.env.REACT_APP_BACKEND_URL;
const FOCUS_STORAGE_KEY = 'dispatch.mapFocusTech';

// ─── Helpers ────────────────────────────────────────────────────────────────

function metersToMiles(m) { return m ? m / 1609.344 : 0; }

function fmtMiles(m) {
  const mi = metersToMiles(m);
  if (mi < 0.1) return '0 mi';
  if (mi < 10) return `${mi.toFixed(1)} mi`;
  return `${Math.round(mi)} mi`;
}

function fmtDuration(minutes) {
  if (!minutes || minutes <= 0) return '0m';
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function fmtEtaClock(minutes) {
  if (!minutes || minutes <= 0) return '—';
  const end = new Date(Date.now() + minutes * 60_000);
  return end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function dateKey(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// SVG numbered pin — matches the technician's polyline color so the map
// reads instantly. Selected pins get a subtle ring + larger size.
function buildPinIcon(google, color, label, opts = {}) {
  const { selected = false, dimmed = false } = opts;
  const size = selected ? 42 : 36;
  const stroke = selected ? '#0f172a' : '#ffffff';
  const ringOpacity = selected ? 0.35 : 0.12;
  const svg = `
    <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 36 50' width='${size}' height='${size + 8}'>
      <defs>
        <filter id='dropshadow' x='-30%' y='-30%' width='160%' height='160%'>
          <feDropShadow dx='0' dy='2' stdDeviation='1.6' flood-opacity='0.32'/>
        </filter>
        <radialGradient id='glow-${label}' cx='50%' cy='35%' r='65%'>
          <stop offset='0%' stop-color='#ffffff' stop-opacity='0.85'/>
          <stop offset='100%' stop-color='#ffffff' stop-opacity='0'/>
        </radialGradient>
      </defs>
      ${selected ? `<circle cx='18' cy='17' r='17' fill='${color}' opacity='${ringOpacity}'/>` : ''}
      <g filter='url(#dropshadow)' opacity='${dimmed ? 0.35 : 1}'>
        <path d='M18 0c9.94 0 18 7.92 18 17.69C36 30.62 18 50 18 50S0 30.62 0 17.69C0 7.92 8.06 0 18 0z'
              fill='${color}' stroke='${stroke}' stroke-width='${selected ? 2 : 1.4}'/>
        <circle cx='18' cy='17' r='11' fill='white'/>
        <circle cx='18' cy='17' r='11' fill='url(#glow-${label})'/>
        <text x='18' y='21' text-anchor='middle' font-family='Inter, -apple-system, sans-serif'
              font-size='13' font-weight='800' fill='${color}'>${label}</text>
      </g>
    </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new google.maps.Size(size, size + 8),
    anchor: new google.maps.Point(size / 2, size + 6),
  };
}

function escapeHtml(s) {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// Premium map style — minimal, low-saturation; emphasizes roads + water without
// competing with the polylines.
const PREMIUM_MAP_STYLE = [
  { elementType: 'geometry', stylers: [{ color: '#f7f8fa' }] },
  { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5e6470' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f7f8fa' }] },
  { featureType: 'administrative.land_parcel', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#eef0f3' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#9aa1ad' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#dde7d8' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#6b7280' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#e6dfca' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#4b5563' }] },
  { featureType: 'transit.line', elementType: 'geometry', stylers: [{ color: '#e5e7eb' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#bfd2db' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#7c8b94' }] },
];

// ─── Main component ────────────────────────────────────────────────────────

export default function MapView({ companyId, dispatchDate }) {
  const navigate = useNavigate();
  const { google, loading: mapsLoading, error: mapsError } = useGoogleMaps();
  const { getDirections } = useDirectionsCache(google);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Per-technician focus, persisted across sessions. Null = "show all routes".
  const [activeTech, setActiveTech] = useState(() => {
    try { return localStorage.getItem(FOCUS_STORAGE_KEY) || null; } catch { return null; }
  });
  const persistFocus = useCallback((uid) => {
    setActiveTech(uid);
    try {
      if (uid) localStorage.setItem(FOCUS_STORAGE_KEY, uid);
      else localStorage.removeItem(FOCUS_STORAGE_KEY);
    } catch (_) { /* ignore quota */ }
  }, []);

  // Currently selected stop drives the Action Card overlay.
  const [selectedStop, setSelectedStop] = useState(null); // { techUid, taskId } | null

  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const overlayRefs = useRef({ markers: [], polylines: [], markerByStop: new Map() });

  // ── Fetch board state ────────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    if (!companyId || !dispatchDate) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.get(`${API}/api/dispatch/route-map`, {
        params: { companyId, date: dateKey(dispatchDate) },
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('[MapView] fetch failed:', err);
      setError(err?.response?.data?.detail || err.message || 'Could not load routes');
    } finally {
      setLoading(false);
    }
  }, [companyId, dispatchDate]);

  // eslint-disable-next-line react-hooks/set-state-in-effect -- Standard data-fetch effect; refresh() owns its setLoading/setError/setData lifecycle.
  useEffect(() => { refresh(); }, [refresh]);

  // If the persisted tech disappears from the response, ignore it on render
  // (we compute the effective focus below). We do NOT auto-clear localStorage —
  // the tech might just be off today; tomorrow they'll show up and the
  // operator's preference will still apply.

  // ── Compute points + bounds ──────────────────────────────────────────────
  const effectiveActiveTech = useMemo(() => {
    if (!data || !activeTech) return null;
    const exists = data.routes?.some((r) => r.techUid === activeTech);
    return exists ? activeTech : null;
  }, [data, activeTech]);

  const visibleRoutes = useMemo(() => {
    if (!data) return [];
    if (!effectiveActiveTech) return data.routes || [];
    const focused = (data.routes || []).filter((r) => r.techUid === effectiveActiveTech);
    const others = (data.routes || []).filter((r) => r.techUid !== effectiveActiveTech);
    return [...focused, ...others];
  }, [data, effectiveActiveTech]);

  const focusedRoute = useMemo(() => {
    if (!data || !effectiveActiveTech) return null;
    return (data.routes || []).find((r) => r.techUid === effectiveActiveTech) || null;
  }, [data, effectiveActiveTech]);

  const boundsPoints = useMemo(() => {
    if (!data) return [];
    const pts = [];
    // When focused, fit to that tech's stops only (so they fill the canvas).
    const sourceRoutes = focusedRoute ? [focusedRoute] : data.routes || [];
    sourceRoutes.forEach((r) => {
      r.stops.forEach((s) => {
        if (typeof s.lat === 'number' && typeof s.lng === 'number') {
          pts.push({ lat: s.lat, lng: s.lng });
        }
      });
    });
    return pts;
  }, [data, focusedRoute]);

  const bounds = useMapBounds(google, boundsPoints);

  // ── Initialize map once ─────────────────────────────────────────────────
  useEffect(() => {
    if (!google?.maps || !mapDivRef.current || mapRef.current) return;
    mapRef.current = new google.maps.Map(mapDivRef.current, {
      zoom: 11,
      center: { lat: 39.83, lng: -98.58 },
      mapTypeControl: false,
      fullscreenControl: true,
      streetViewControl: false,
      styles: PREMIUM_MAP_STYLE,
      backgroundColor: '#f7f8fa',
      gestureHandling: 'greedy',
    });
    // Click outside markers closes the Action Card.
    mapRef.current.addListener('click', () => setSelectedStop(null));
  }, [google]);

  // Fit bounds whenever data changes
  useEffect(() => {
    if (!mapRef.current || !bounds) return;
    mapRef.current.fitBounds(bounds, 100);
  }, [bounds]);

  // ── Render markers + polylines ───────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!google?.maps || !map || !data) return;

    // Wipe previous overlays
    overlayRefs.current.markers.forEach((m) => m.setMap(null));
    overlayRefs.current.polylines.forEach((p) => p.setMap(null));
    overlayRefs.current.markers = [];
    overlayRefs.current.polylines = [];
    overlayRefs.current.markerByStop = new Map();

    visibleRoutes.forEach((route) => {
      const dimmed = effectiveActiveTech && effectiveActiveTech !== route.techUid;

      route.stops.forEach((stop) => {
        if (typeof stop.lat !== 'number' || typeof stop.lng !== 'number') return;
        const isSelected = selectedStop?.taskId === stop.taskId;
        const icon = buildPinIcon(google, route.color, String(stop.order), {
          selected: isSelected,
          dimmed,
        });
        const marker = new google.maps.Marker({
          position: { lat: stop.lat, lng: stop.lng },
          map,
          icon,
          zIndex: isSelected ? 500 : (dimmed ? 1 : 50),
          animation: isSelected ? google.maps.Animation.BOUNCE : null,
        });
        if (isSelected) {
          // Bounce just once for a quick attention cue, then stop so it
          // doesn't distract during a long Action Card review.
          setTimeout(() => marker.setAnimation(null), 1400);
        }
        marker.addListener('click', (e) => {
          if (e?.domEvent?.stopPropagation) e.domEvent.stopPropagation();
          setSelectedStop({ techUid: route.techUid, taskId: stop.taskId });
        });
        overlayRefs.current.markers.push(marker);
        overlayRefs.current.markerByStop.set(stop.taskId, marker);
      });

      // Draw polyline via Directions service (road geometry) with straight-line fallback
      const waypoints = route.stops
        .filter((s) => typeof s.lat === 'number' && typeof s.lng === 'number')
        .map((s) => ({ lat: s.lat, lng: s.lng }));
      if (waypoints.length < 2) return;

      getDirections(waypoints).then((dirResult) => {
        if (!dirResult) return;
        const path = dirResult.routes?.[0]?.overview_path;
        if (!path) return;
        // Two-layer polyline: a soft halo + the sharp colored line. Premium feel.
        const halo = new google.maps.Polyline({
          path,
          map,
          strokeColor: '#ffffff',
          strokeOpacity: dimmed ? 0.0 : 0.75,
          strokeWeight: dimmed ? 0 : 9,
          geodesic: true,
          zIndex: dimmed ? 0 : 8,
        });
        const main = new google.maps.Polyline({
          path,
          map,
          strokeColor: route.color,
          strokeOpacity: dimmed ? 0.18 : 0.95,
          strokeWeight: dimmed ? 3 : 5,
          geodesic: true,
          zIndex: dimmed ? 1 : 10,
        });
        overlayRefs.current.polylines.push(halo, main);
      }).catch(() => {
        // Fallback: dashed straight line between stops
        const poly = new google.maps.Polyline({
          path: waypoints.map((p) => new google.maps.LatLng(p.lat, p.lng)),
          map,
          strokeColor: route.color,
          strokeOpacity: 0,
          strokeWeight: 0,
          icons: [{
            icon: { path: 'M 0,-1 0,1', strokeOpacity: dimmed ? 0.25 : 1, scale: 3, strokeColor: route.color },
            offset: '0',
            repeat: '14px',
          }],
        });
        overlayRefs.current.polylines.push(poly);
      });
    });
  }, [google, data, visibleRoutes, effectiveActiveTech, getDirections, selectedStop]);

  // Resolve the selected stop's full data for the Action Card
  const selectedStopFull = useMemo(() => {
    if (!data || !selectedStop) return null;
    const route = data.routes.find((r) => r.techUid === selectedStop.techUid);
    if (!route) return null;
    const stop = route.stops.find((s) => s.taskId === selectedStop.taskId);
    if (!stop) return null;
    return { route, stop };
  }, [data, selectedStop]);

  const totals = data?.grandTotals || {};
  const hasAnyRoutes = (data?.routes?.length || 0) > 0;
  const hasAnyStops = (totals.stops || 0) > 0;

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex-1 flex flex-col min-h-0 bg-gray-50" data-testid="dispatch-map-view">
      <MetricsBar
        totals={totals}
        unassignedJobCount={data?.unassignedJobCount}
        loading={loading}
        onRefresh={refresh}
      />

      <MapDiagnosticsBanner
        diagnostics={data?.diagnostics}
        dateKey={dateKey(dispatchDate)}
      />

      <div className="flex-1 flex min-h-0 flex-col lg:flex-row">
        <LegendPanel
          routes={data?.routes || []}
          loading={loading && !data}
          activeTech={effectiveActiveTech}
          onSelect={persistFocus}
          onPickStop={(uid, taskId) => setSelectedStop({ techUid: uid, taskId })}
        />

        {/* Map canvas */}
        <div className="flex-1 relative min-w-0 min-h-[480px]">
          {(mapsLoading || (loading && !data)) && (
            <div className="absolute inset-0 z-10 bg-white/40 backdrop-blur-sm flex items-center justify-center pointer-events-none">
              <div className="px-4 py-2 rounded-md bg-white shadow-md text-xs text-gray-700 flex items-center gap-2 animate-in fade-in duration-300">
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                {mapsLoading ? 'Loading map…' : 'Updating routes…'}
              </div>
            </div>
          )}
          {(mapsError || error) && (
            <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 px-4 py-2 rounded-md border border-red-200 bg-red-50 text-xs text-red-800 flex items-center gap-2 shadow-sm">
              <AlertTriangle className="h-3.5 w-3.5" />
              {mapsError?.message || error}
            </div>
          )}

          {/* Active-focus chip in top-left */}
          {effectiveActiveTech && data && focusedRoute && (
            <div className="absolute top-3 left-3 z-20 animate-in fade-in slide-in-from-top-2 duration-300">
              <button
                type="button"
                onClick={() => persistFocus(null)}
                className="bg-white shadow-md border border-gray-200 rounded-full px-3 py-1.5 text-[11px] font-semibold flex items-center gap-2 hover:bg-gray-50 transition-colors"
                data-testid="map-clear-focus"
              >
                <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: focusedRoute.color }} />
                Focused: {focusedRoute.techName}
                <X className="h-3 w-3 text-gray-500" />
              </button>
            </div>
          )}

          {/* Empty state — only when zero routes for the date */}
          {!loading && data && !hasAnyRoutes && (
            <EmptyMapState date={dispatchDate} />
          )}
          {!loading && data && hasAnyRoutes && !hasAnyStops && (
            <EmptyMapState date={dispatchDate} variant="no-stops" />
          )}

          <div ref={mapDivRef} className="absolute inset-0" data-testid="map-canvas" />

          {/* Action Card overlay */}
          {selectedStopFull && (
            <ActionCard
              route={selectedStopFull.route}
              stop={selectedStopFull.stop}
              onClose={() => setSelectedStop(null)}
              navigate={navigate}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Subcomponents ─────────────────────────────────────────────────────────

function MetricsBar({ totals, unassignedJobCount, loading, onRefresh }) {
  return (
    <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 sm:gap-5 flex-wrap">
      <MetricChip
        icon={<MapPin className="h-4 w-4" />}
        label="Stops"
        value={totals.stops ?? 0}
        accentBg="bg-slate-50"
        accentText="text-slate-900"
        testId="map-metric-stops"
      />
      <MetricChip
        icon={<Gauge className="h-4 w-4" />}
        label="Mileage"
        value={fmtMiles(totals.driveMeters ?? 0)}
        accentBg="bg-indigo-50"
        accentText="text-indigo-700"
        testId="map-metric-mileage"
      />
      <MetricChip
        icon={<Clock className="h-4 w-4" />}
        label="Drive time"
        value={fmtDuration(totals.driveMin ?? 0)}
        accentBg="bg-amber-50"
        accentText="text-amber-700"
        testId="map-metric-drive"
      />
      <MetricChip
        icon={<Wrench className="h-4 w-4" />}
        label="Service time"
        value={fmtDuration(totals.serviceMin ?? 0)}
        accentBg="bg-emerald-50"
        accentText="text-emerald-700"
        testId="map-metric-service"
      />
      <MetricChip
        icon={<CalendarClock className="h-4 w-4" />}
        label="Est. completion"
        value={fmtEtaClock(totals.estimatedCompletionMin ?? 0)}
        accentBg="bg-purple-50"
        accentText="text-purple-700"
        testId="map-metric-completion"
      />
      <MetricChip
        icon={<Users className="h-4 w-4" />}
        label="Techs"
        value={totals.techCount ?? 0}
        accentBg="bg-gray-50"
        accentText="text-gray-700"
        testId="map-metric-techs"
      />

      <div className="ml-auto flex items-center gap-2">
        {unassignedJobCount > 0 && (
          <span
            className="text-[11px] font-medium px-2.5 py-1 rounded-full border border-amber-200 bg-amber-50 text-amber-800 flex items-center gap-1"
            data-testid="map-unassigned-count"
          >
            <AlertTriangle className="h-3 w-3" />
            {unassignedJobCount} unassigned
          </span>
        )}
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="h-8 px-3 text-xs font-medium border border-gray-200 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center gap-1.5 active:scale-95"
          data-testid="map-refresh-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>
    </div>
  );
}

function MetricChip({ icon, label, value, accentBg, accentText, testId }) {
  return (
    <div className="flex items-center gap-2.5" data-testid={testId}>
      <div className={`h-9 w-9 rounded-lg ${accentBg} ${accentText} flex items-center justify-center ring-1 ring-inset ring-black/[.04]`}>
        {icon}
      </div>
      <div className="flex flex-col leading-tight">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</span>
        <span className={`text-sm font-bold ${accentText}`}>{value}</span>
      </div>
    </div>
  );
}

function LegendPanel({ routes, loading, activeTech, onSelect, onPickStop }) {
  return (
    <aside
      className="w-full lg:w-72 lg:shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 bg-white overflow-y-auto max-h-72 lg:max-h-none"
      data-testid="map-legend"
    >
      <div className="p-3 border-b border-gray-100 sticky top-0 bg-white z-10">
        <button
          type="button"
          onClick={() => onSelect(null)}
          className={`w-full px-3 py-2 rounded-md text-xs font-semibold transition-all active:scale-[0.98] ${
            activeTech === null
              ? 'bg-gray-900 text-white shadow-sm'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
          data-testid="map-legend-all"
        >
          {activeTech === null ? '✓ Showing all routes' : 'Show all routes'}
        </button>
      </div>
      {loading && (
        <div className="p-3 space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="h-4 bg-gray-100 rounded w-2/3 mb-2" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      )}
      {!loading && routes.length === 0 && (
        <div className="p-6 text-center text-xs text-gray-500">
          No technicians have routes for this day.
        </div>
      )}
      <div className="divide-y divide-gray-100">
        {routes.map((route) => {
          const isActive = activeTech === route.techUid;
          const handleToggle = () => onSelect(isActive ? null : route.techUid);
          return (
            <div
              key={route.techUid}
              role="button"
              tabIndex={0}
              onClick={handleToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle();
                }
              }}
              className={`group w-full text-left p-3 transition-all cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 ${
                isActive
                  ? 'bg-gradient-to-r from-gray-50 to-white'
                  : 'hover:bg-gray-50'
              }`}
              data-testid={`map-legend-tech-${route.techUid}`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`inline-block rounded-full shrink-0 ring-2 ring-white shadow transition-all ${
                    isActive ? 'w-3.5 h-3.5 scale-110' : 'w-3 h-3'
                  }`}
                  style={{ backgroundColor: route.color }}
                />
                <span className="text-sm font-semibold text-gray-900 truncate flex-1">
                  {route.techName}
                </span>
                {isActive && (
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 animate-in fade-in zoom-in duration-300" />
                )}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-1 text-[10px] text-gray-600">
                <div>
                  <div className="font-bold text-gray-900 text-xs">{route.totals.stops}</div>
                  <div className="uppercase tracking-wider text-[9px] text-gray-500">stops</div>
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-xs">{fmtMiles(route.totals.driveMeters)}</div>
                  <div className="uppercase tracking-wider text-[9px] text-gray-500">miles</div>
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-xs">{fmtDuration(route.totals.driveMin)}</div>
                  <div className="uppercase tracking-wider text-[9px] text-gray-500">drive</div>
                </div>
              </div>
              {route.stops.length > 0 && isActive && (
                <div className="mt-3 space-y-1 max-h-44 overflow-y-auto pr-1">
                  {route.stops.map((s) => (
                    <button
                      key={s.taskId}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPickStop(route.techUid, s.taskId);
                      }}
                      className="w-full text-left rounded-md px-2 py-1.5 hover:bg-white border border-transparent hover:border-gray-200 transition-all flex items-center gap-2"
                      data-testid={`map-legend-stop-${s.taskId}`}
                    >
                      <span
                        className="shrink-0 w-5 h-5 rounded-full text-[10px] font-bold text-white flex items-center justify-center shadow-sm"
                        style={{ backgroundColor: route.color }}
                      >
                        {s.order}
                      </span>
                      <span className="text-[11px] text-gray-700 truncate flex-1">{s.clientName}</span>
                      {s.isFixedAppointment && (
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                          Appt
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {!isActive && route.stops.length > 0 && (
                <div className="mt-2 text-[10px] text-gray-500 flex items-center gap-1 truncate">
                  <MapPinned className="h-2.5 w-2.5 shrink-0" />
                  <span className="truncate">
                    {route.stops[0].clientName}
                    {route.stops.length > 1 && ` · +${route.stops.length - 1} more`}
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
}

function EmptyMapState({ date, variant }) {
  const dateLabel = date
    ? new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
    : 'this date';
  const isNoStops = variant === 'no-stops';
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
      <div className="text-center max-w-sm px-6 py-8 bg-white/80 backdrop-blur-md rounded-2xl border border-gray-200 shadow-sm pointer-events-auto">
        <div className="mx-auto w-14 h-14 rounded-full bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center mb-3">
          <Compass className="h-7 w-7 text-indigo-500" />
        </div>
        <h3 className="text-sm font-semibold text-gray-900">
          {isNoStops ? 'Technicians have no stops yet' : `No routes for ${dateLabel}`}
        </h3>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          {isNoStops
            ? 'Switch to the List view to assign jobs and they\u2019ll appear on the map automatically.'
            : 'Assign jobs to technicians, or use Build Day to auto-plan optimal routes.'}
        </p>
      </div>
    </div>
  );
}

function ActionCard({ route, stop, onClose, navigate }) {
  const phone = stop.phone || null;
  const navUrl = stop.lat && stop.lng
    ? `https://www.google.com/maps/dir/?api=1&destination=${stop.lat},${stop.lng}&travelmode=driving`
    : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(stop.address || '')}&travelmode=driving`;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(stop.address || '');
      toast.success('Address copied');
    } catch (err) {
      toast.error('Could not copy');
    }
  }, [stop.address]);

  const handleOpenCustomer = useCallback(() => {
    if (stop.leadId) navigate(`/leads/${stop.leadId}`);
  }, [stop.leadId, navigate]);

  const handleOpenWorkOrder = useCallback(() => {
    if (stop.leadId) {
      // Lead profile renders the work order in context; query hint lets a
      // future enhancement scroll to / auto-open the specific task.
      navigate(`/leads/${stop.leadId}?taskId=${encodeURIComponent(stop.taskId)}`);
    }
  }, [stop.leadId, stop.taskId, navigate]);

  return (
    <div
      className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 w-[min(560px,calc(100%-2rem))] animate-in fade-in slide-in-from-bottom-4 duration-300"
      data-testid="map-action-card"
    >
      <div className="bg-white rounded-2xl shadow-[0_20px_50px_-10px_rgba(0,0,0,0.25)] border border-gray-200 overflow-hidden">
        {/* Header — colored stripe matches the technician */}
        <div
          className="h-1 w-full"
          style={{ background: `linear-gradient(90deg, ${route.color} 0%, ${route.color}99 100%)` }}
        />
        <div className="p-4">
          <div className="flex items-start gap-3">
            <span
              className="shrink-0 w-9 h-9 rounded-xl text-sm font-bold text-white flex items-center justify-center shadow-md"
              style={{ backgroundColor: route.color }}
            >
              {stop.order}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="text-sm font-bold text-gray-900 truncate" data-testid="action-card-name">
                  {stop.clientName || 'Customer'}
                </h4>
                {stop.isFixedAppointment && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded">
                    Appointment
                  </span>
                )}
                <span
                  className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ color: route.color, backgroundColor: `${route.color}1a` }}
                >
                  {route.techName}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-0.5 truncate" data-testid="action-card-address">
                {stop.address}
              </p>
              <div className="mt-2 flex items-center gap-3 text-[11px] text-gray-600 flex-wrap">
                {stop.legMin > 0 && (
                  <span className="flex items-center gap-1">
                    <Navigation className="h-3 w-3 text-gray-400" />
                    +{stop.legMin}m drive
                  </span>
                )}
                {stop.serviceMin > 0 && (
                  <span className="flex items-center gap-1">
                    <Wrench className="h-3 w-3 text-gray-400" />
                    ~{stop.serviceMin}m service
                  </span>
                )}
                {stop.scheduledStart && (
                  <span className="flex items-center gap-1">
                    <CalendarClock className="h-3 w-3 text-gray-400" />
                    {new Date(stop.scheduledStart).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 h-7 w-7 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 flex items-center justify-center transition-colors"
              data-testid="action-card-close"
              aria-label="Close"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Action grid */}
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
            <ActionBtn
              icon={<User className="h-3.5 w-3.5" />}
              label="Customer"
              onClick={handleOpenCustomer}
              disabled={!stop.leadId}
              testId="action-open-customer"
            />
            <ActionBtn
              icon={<ExternalLink className="h-3.5 w-3.5" />}
              label="Work Order"
              onClick={handleOpenWorkOrder}
              disabled={!stop.leadId}
              testId="action-open-workorder"
            />
            <ActionBtn
              icon={<Phone className="h-3.5 w-3.5" />}
              label="Call"
              onClick={() => { window.location.href = `tel:${phone}`; }}
              disabled={!phone}
              testId="action-call-customer"
              href={phone ? `tel:${phone}` : undefined}
            />
            <ActionBtn
              icon={<Navigation className="h-3.5 w-3.5" />}
              label="Navigate"
              href={navUrl}
              targetBlank
              testId="action-navigate"
            />
            <ActionBtn
              icon={<ClipboardCopy className="h-3.5 w-3.5" />}
              label="Copy"
              onClick={handleCopy}
              disabled={!stop.address}
              testId="action-copy-address"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, onClick, disabled, href, targetBlank, testId }) {
  const cls = `flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-[11px] font-semibold border transition-all active:scale-95
    ${disabled
      ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50 hover:shadow-sm'}`;
  if (href && !disabled) {
    return (
      <a
        href={href}
        onClick={onClick}
        target={targetBlank ? '_blank' : undefined}
        rel={targetBlank ? 'noopener noreferrer' : undefined}
        className={cls}
        data-testid={testId}
      >
        {icon}
        <span className="truncate">{label}</span>
      </a>
    );
  }
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={cls}
      data-testid={testId}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}
